import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { boreholesApi } from '../../lib/api';
import { lithologyColor, lithologyKey, loadColorOverrides, saveColorOverrides } from '../../lib/boreholes';

const FORMAT_LABELS = { csv: 'CSV (collars / intervals)', las: 'LAS 2.0' };
const fmt = (v, d = 2) => (v === null || v === undefined ? '—' : Number(v).toFixed(d));

/** Thin horizontal lithology bar for the table (top of hole on the left). */
function LithologyBar({ intervals = [], total, overrides }) {
  const depth = total || intervals.reduce((m, iv) => Math.max(m, iv.depth_to), 0) || 1;
  return (
    <svg width={120} height={10} aria-hidden="true">
      <rect x={0} y={0} width={120} height={10} fill="none" stroke="currentColor" strokeOpacity={0.3} />
      {intervals.map((iv, i) => (
        <rect key={i} x={(iv.depth_from / depth) * 120} y={0} height={10}
          width={Math.max(0.5, ((iv.depth_to - iv.depth_from) / depth) * 120)}
          fill={lithologyColor(iv.lithology, iv.color, overrides)} />
      ))}
    </svg>
  );
}

/**
 * Dataset page card for datasets of method Borehole (docs/BOREHOLES.md): import status
 * (datasets.metadata.boreholes), (re-)import from the stored file, the imported holes,
 * and per-viewer lithology colour overrides.
 */
export default function BoreholesCard({ dataset, onImported, onShowOnMap, showToast }) {
  const [holes, setHoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [overrides, setOverrides] = useState(() => loadColorOverrides());
  const status = dataset?.metadata?.boreholes;
  const datasetId = dataset?.id;

  const load = useCallback(async () => {
    if (!datasetId) return;
    setLoading(true);
    try {
      const res = await boreholesApi.list({ dataset_id: datasetId, include_intervals: true, limit: 5000 });
      setHoles(res.data?.boreholes || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load boreholes');
    } finally {
      setLoading(false);
    }
  }, [datasetId]);

  useEffect(() => { load(); }, [load]);

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await boreholesApi.importDataset(datasetId);
      showToast?.(`Imported ${res.data.n_boreholes} borehole(s), ${res.data.n_intervals} interval(s), ${res.data.n_logs} log(s)`, 'success');
      await load();
      onImported?.();
    } catch (e) {
      showToast?.(e?.response?.data?.detail || 'Borehole import failed', 'error');
    } finally {
      setImporting(false);
    }
  };

  const lithologies = useMemo(() => {
    const seen = new Map();
    holes.forEach(h => (h.intervals || []).forEach((iv) => {
      const name = (iv.lithology || '').trim();
      if (name && !seen.has(name.toLowerCase())) seen.set(name.toLowerCase(), { name, stored: iv.color });
    }));
    return [...seen.entries()].slice(0, 40);
  }, [holes]);

  const setOverride = (key, color) => {
    const next = { ...overrides, [key]: color };
    setOverrides(next);
    saveColorOverrides(next);
  };

  const warnings = Array.isArray(status?.warnings) ? status.warnings : [];
  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="boreholes-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Boreholes</h2>
        <div className="flex gap-2">
          {onShowOnMap && holes.length > 0 && (
            <button onClick={onShowOnMap} className="px-3 py-1 text-xs border border-border rounded-md hover:bg-muted"
              data-testid="boreholes-show-on-map">
              Show on map
            </button>
          )}
          <button onClick={handleImport} disabled={importing}
            className="px-3 py-1 text-xs border border-border rounded-md hover:bg-muted disabled:opacity-50"
            data-testid="boreholes-import">
            {importing ? 'Importing...' : status ? 'Re-import file' : 'Import file'}
          </button>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div><dt className="text-muted-foreground">Status</dt>
          <dd className="font-semibold">
            {status?.error ? 'Import failed' : status?.pending ? 'Waiting for import' : status ? `Imported ${new Date(status.imported_at).toLocaleString()}` : 'Not imported'}
          </dd></div>
        <div><dt className="text-muted-foreground">Format</dt>
          <dd className="font-semibold">{FORMAT_LABELS[status?.format] || status?.format || '—'}</dd></div>
        <div><dt className="text-muted-foreground">Boreholes</dt><dd className="font-semibold">{loading ? <span data-testid="boreholes-count-skeleton" className="inline-block h-4 w-8 bg-muted rounded animate-pulse align-middle" /> : holes.length}</dd></div>
        <div><dt className="text-muted-foreground">Intervals / logs</dt>
          <dd className="font-semibold">{status ? `${status.n_intervals ?? 0} / ${status.n_logs ?? 0}` : '—'}</dd></div>
      </dl>

      {warnings.length > 0 && (
        <ul className="mb-4 text-xs text-yellow-800 bg-yellow-100 rounded p-3 list-disc list-inside" data-testid="boreholes-warnings">
          {warnings.slice(0, 10).map(w => <li key={w}>{w}</li>)}
          {(status.n_warnings || warnings.length) > 10 && <li>{(status.n_warnings || warnings.length) - 10} more</li>}
        </ul>
      )}
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : holes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No boreholes from this dataset yet. CSV (name, lon, lat, elevation, depth_from, depth_to, lithology) and LAS 2.0 files are supported.
        </p>
      ) : (
        <div className="overflow-auto max-h-80">
          <table className="w-full text-xs" data-testid="boreholes-table">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-2">Name</th><th className="pr-2">Lon</th><th className="pr-2">Lat</th>
                <th className="pr-2">Elev (m)</th><th className="pr-2">Depth (m)</th><th className="pr-2">Lithology</th>
              </tr>
            </thead>
            <tbody>
              {holes.map(h => (
                <tr key={h.id} className="border-t border-border">
                  <td className="py-1 pr-2 font-medium">{h.name}</td>
                  <td className="pr-2 monospace">{fmt(h.lon, 6)}</td>
                  <td className="pr-2 monospace">{fmt(h.lat, 6)}</td>
                  <td className="pr-2">{fmt(h.elevation)}</td>
                  <td className="pr-2">{fmt(h.total_depth)}</td>
                  <td className="pr-2"><LithologyBar intervals={h.intervals} total={h.total_depth} overrides={overrides} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lithologies.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold text-muted-foreground mb-2">Lithology colours (this browser)</h3>
          <div className="flex flex-wrap gap-3">
            {lithologies.map(([key, { name, stored }]) => (
              <label key={key} className="flex items-center gap-1 text-xs">
                <input type="color" value={lithologyColor(name, stored, overrides)}
                  onChange={e => setOverride(lithologyKey(name) || key, e.target.value)}
                  className="w-5 h-5 p-0 border-0 bg-transparent" />
                {name}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
